import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { psychiatryAPI } from '../../utils/api';

const SUPERVISORS = ['Dr. Sana Khan', 'Dr. Ayesha Farooq', 'Dr. Nadia Ahmed', 'Dr. Usman Malik'];
const COUNSELLORS = ['Dr. Sana Khan', 'Dr. Ayesha Farooq', 'Dr. Nadia Ahmed', 'Dr. Usman Malik', 'Dr. Fatima Rizvi'];
const SESSION_FOCUS_OPTIONS = [
    'Case review', 'Assessment skills', 'Risk management', 'Intervention delivery',
    'Record keeping', 'Rapport building', 'Cultural competency', 'Ethics & boundaries',
    'Professional development', 'Counsellor wellbeing', 'Administrative issues',
];
const COMPETENCY_AREAS = ['Assessment skills', 'Risk management', 'Intervention delivery', 'Record keeping', 'Rapport building', 'Cultural competency', 'Ethics & boundaries', 'Commitment to supervision'];
const DEVELOPMENT_AREAS = [...COMPETENCY_AREAS, 'Time management', 'Documentation', 'Team collaboration'];

export default function PsychSupervision() {
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        supervision_date: new Date().toISOString().split('T')[0],
        supervision_mode: 'Individual (in-person)',
        supervisor_name: '',
        supervisee_names: [],
        num_supervisees: 1,
        duration: '1 hour',
        session_focus: [],
        total_caseload: '',
        cases_discussed: '',
        case_summary: '',
        competency_areas: {},
        areas_of_development: [],
        training_needs: [],
        short_term_goals: '',
        long_term_goals: '',
        supervisor_reflection: '',
    });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };
    const ta = { ...inp, resize: 'vertical', minHeight: 70 };
    const lbl = { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' };

    const load = async () => {
        try {
            const res = await psychiatryAPI.getSupervision();
            setRecords(res.data);
        } catch { setRecords([]); } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggleArr = (key, val) => setForm(f => ({
        ...f, [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val]
    }));

    const setCompetency = (area, rating) => setForm(f => ({ ...f, competency_areas: { ...f.competency_areas, [area]: rating } }));

    const submit = async () => {
        if (!form.supervisor_name) { toast.error('Please enter supervisor name'); return; }
        setSaving(true);
        try {
            await psychiatryAPI.addSupervision(form);
            toast.success('Supervision record saved');
            setShowForm(false);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); } finally { setSaving(false); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <button onClick={() => navigate('/psych')} style={{ padding: '5px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>← Back</button>
                        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#7c3aed', margin: 0 }}>📋 Clinical Supervision Log</h2>
                    </div>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Counsellor supervision records & competency tracking</p>
                </div>
                <button onClick={() => setShowForm(true)}
                    style={{ padding: '8px 18px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    + Log Supervision
                </button>
            </div>

            {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            : records.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
                    <p style={{ color: '#64748b' }}>No supervision records yet</p>
                    <button onClick={() => setShowForm(true)} style={{ marginTop: 8, padding: '8px 20px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}>Log First Supervision</button>
                </div>
            ) : (
                <div>
                    {records.map(r => (
                        <div key={r.id} style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 16, marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                                <div>
                                    <div style={{ fontWeight: 800, color: '#7c3aed', fontSize: 14 }}>
                                        {new Date(r.supervision_date).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' })} — {r.supervision_mode}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                        Supervisor: <strong>{r.supervisor_name}</strong> | Duration: {r.duration} | Supervisees: {r.num_supervisees}
                                    </div>
                                </div>
                                <div style={{ fontSize: 11, background: '#f5f3ff', color: '#7c3aed', padding: '4px 10px', borderRadius: 10, fontWeight: 600 }}>
                                    {r.cases_discussed}/{r.total_caseload} cases discussed
                                </div>
                            </div>
                            {r.supervisee_names?.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>SUPERVISEES: </span>
                                    {r.supervisee_names.map(n => <span key={n} style={{ background: '#f5f3ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginRight: 4 }}>{n}</span>)}
                                </div>
                            )}
                            {r.session_focus?.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>FOCUS: </span>
                                    {r.session_focus.map(f => <span key={f} style={{ background: '#f0fdf4', color: '#16a34a', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginRight: 4 }}>{f}</span>)}
                                </div>
                            )}
                            {r.case_summary && <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}><strong>Case Summary:</strong> {r.case_summary}</div>}
                            {r.supervisor_reflection && <div style={{ fontSize: 12, color: '#374151', background: '#f8fafc', borderRadius: 6, padding: 8, marginTop: 6 }}><strong>Supervisor Reflection:</strong> {r.supervisor_reflection}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Add Supervision Modal */}
            {showForm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 750, maxHeight: '92vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, color: '#7c3aed' }}>📋 Log Supervision Session</h3>
                            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
                        </div>

                        {/* Basic Info */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                            <div><label style={lbl}>Date</label><input style={inp} type="date" value={form.supervision_date} onChange={e => set('supervision_date', e.target.value)} /></div>
                            <div><label style={lbl}>Mode</label>
                                <select style={inp} value={form.supervision_mode} onChange={e => set('supervision_mode', e.target.value)}>
                                    {['Individual (in-person)', 'Individual (remote)', 'Group Supervision (in-person)', 'Group Supervision (remote)'].map(m => <option key={m}>{m}</option>)}
                                </select>
                            </div>
                            <div><label style={lbl}>Duration</label>
                                <select style={inp} value={form.duration} onChange={e => set('duration', e.target.value)}>
                                    {['30 min', '1 hour', '2 hours', '3 hours'].map(d => <option key={d}>{d}</option>)}
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                            <div><label style={lbl}>Supervisor Name *</label>
                                <select style={inp} value={form.supervisor_name} onChange={e => set('supervisor_name', e.target.value)}>
                                    <option value="">Select...</option>
                                    {SUPERVISORS.map(s => <option key={s}>{s}</option>)}
                                </select>
                            </div>
                            <div><label style={lbl}>Number of Supervisees</label><input style={inp} type="number" min="1" value={form.num_supervisees} onChange={e => set('num_supervisees', e.target.value)} /></div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={lbl}>Supervisee(s)</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {COUNSELLORS.map(c => (
                                    <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer', background: form.supervisee_names.includes(c) ? '#f5f3ff' : '#f8fafc', padding: '4px 10px', borderRadius: 20, border: `1px solid ${form.supervisee_names.includes(c) ? '#c4b5fd' : '#e2e8f0'}` }}>
                                        <input type="checkbox" checked={form.supervisee_names.includes(c)} onChange={e => toggleArr('supervisee_names', c)} />
                                        {c}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Session Focus */}
                        <div style={{ marginBottom: 12 }}>
                            <label style={lbl}>Session Focus (select all that apply)</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                {SESSION_FOCUS_OPTIONS.map(f => (
                                    <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={form.session_focus.includes(f)} onChange={() => toggleArr('session_focus', f)} />
                                        {f}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Caseload */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                            <div><label style={lbl}>Total Caseload</label><input style={inp} type="number" min="0" value={form.total_caseload} onChange={e => set('total_caseload', e.target.value)} /></div>
                            <div><label style={lbl}>Cases Discussed</label><input style={inp} type="number" min="0" value={form.cases_discussed} onChange={e => set('cases_discussed', e.target.value)} /></div>
                        </div>
                        <div style={{ marginBottom: 12 }}><label style={lbl}>Case Summary</label><textarea style={ta} value={form.case_summary} onChange={e => set('case_summary', e.target.value)} placeholder="Brief summary of cases discussed..." /></div>

                        {/* Competency Ratings */}
                        <div style={{ marginBottom: 12 }}>
                            <label style={lbl}>Competency Ratings (1=Needs Improvement → 5=Excellent)</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {COMPETENCY_AREAS.map(area => (
                                    <div key={area} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: 6, padding: '6px 10px' }}>
                                        <span style={{ fontSize: 12 }}>{area}</span>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            {[1,2,3,4,5].map(n => (
                                                <button key={n} onClick={() => setCompetency(area, n)}
                                                    style={{ width: 24, height: 24, borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                                                        background: form.competency_areas[area] === n ? '#7c3aed' : '#e2e8f0',
                                                        color: form.competency_areas[area] === n ? 'white' : '#64748b' }}>
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Development & Goals */}
                        <div style={{ marginBottom: 12 }}>
                            <label style={lbl}>Areas of Development</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {DEVELOPMENT_AREAS.map(a => (
                                    <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', background: form.areas_of_development.includes(a) ? '#fef3c7' : '#f8fafc', padding: '3px 8px', borderRadius: 12, border: `1px solid ${form.areas_of_development.includes(a) ? '#fcd34d' : '#e2e8f0'}` }}>
                                        <input type="checkbox" checked={form.areas_of_development.includes(a)} onChange={() => toggleArr('areas_of_development', a)} />
                                        {a}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                            <div><label style={lbl}>Short-term Goals</label><textarea style={ta} value={form.short_term_goals} onChange={e => set('short_term_goals', e.target.value)} /></div>
                            <div><label style={lbl}>Long-term Goals</label><textarea style={ta} value={form.long_term_goals} onChange={e => set('long_term_goals', e.target.value)} /></div>
                        </div>
                        <div style={{ marginBottom: 16 }}><label style={lbl}>Supervisor Reflection</label><textarea style={ta} value={form.supervisor_reflection} onChange={e => set('supervisor_reflection', e.target.value)} placeholder="Reflections on supervisee progress / group dynamics..." /></div>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowForm(false)} style={{ padding: '9px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                            <button onClick={submit} disabled={saving} style={{ padding: '9px 24px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700 }}>
                                {saving ? 'Saving...' : '✅ Save Supervision Record'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
